import { api, daysUntil, dueText, fmtDate, kr } from '../api';
import { useAuth, useData } from '../App';
import { useState } from 'react';
import { AttendanceBars, BalanceChart, Empty, HaloRing, Icon, Ring, Stat } from '../ui';

export default function Yfirlit() {
  const { stats, events, finance, todos, art, memberName } = useData();
  const { refresh } = useData();
  const [rolling, setRolling] = useState(false);

  /* Card art is fetched and cached by the server, then served from it —
     Scryfall asks that images not be hotlinked on every page view. */
  const hasArt = art !== null && !('none' in art);
  const credit = hasArt ? art : null;

  async function rollArt() {
    setRolling(true);
    try {
      await api.refreshArt();
      await refresh();
      // bust the browser cache for the image itself
      setArtKey(Date.now());
    } finally {
      setRolling(false);
    }
  }

  const [artKey, setArtKey] = useState(() => Date.now());
  const { user } = useAuth();

  /* What this person personally still owes, soonest first. Anything
     without a date sorts last — a task with no deadline isn't urgent. */
  const mine = todos
    .filter((t) => !t.done && t.assigned_user_id === user?.id)
    .sort((a, b) => (a.due_date ?? '9999').localeCompare(b.due_date ?? '9999'));
  const nextUp = mine.find((t) => t.due_date);
  const overdue = mine.filter((t) => {
    const d = daysUntil(t.due_date);
    return d !== null && d < 0;
  }).length;
  const last = events[0];
  const recent = events.slice(0, 5);

  const week = (() => {
    const now = new Date();
    const jan = new Date(now.getFullYear(), 0, 1);
    return Math.ceil(((now.getTime() - jan.getTime()) / 86_400_000 + jan.getDay() + 1) / 7);
  })();

  return (
    <div className="view">
      <div className="g-hero">
        {/* ART SLOT — set --art on this element to drop a photo behind it */}
        <section
          className={`halo col-4${hasArt ? ' has-art' : ''}`}
          style={hasArt ? { ['--art' as string]: `url(/api/art/image?v=${artKey})` } : undefined}
        >
          {!hasArt && <HaloRing />}

          {credit && (
            <div className="art-credit">
              <span className="ac-line">{credit.card}</span>
              <span className="ac-sub">
                {credit.artist} · {credit.setName}
              </span>
              <button
                type="button"
                className="ac-roll"
                onClick={() => void rollArt()}
                disabled={rolling}
                title="Sækja nýtt listaverk"
                aria-label="Sækja nýtt listaverk"
              >
                <Icon.dice />
              </button>
            </div>
          )}
          <div className="halo-in">
            <span className="lbl gold">Vika {week}</span>
            {last ? (
              <>
                <h2>{last.name}</h2>
                <p>
                  {fmtDate(last.event_date)} · {last.attendees.length} mættu
                  {last.winner_member_id ? ` · sigurvegari ${memberName(last.winner_member_id)}` : ''}
                </p>
              </>
            ) : (
              <>
                <h2>Ekkert skráð enn</h2>
                <p>
                  Skráðu fyrsta viðburðinn til að byrja að safna gögnum. Mætingartölur verða ekki
                  endurgerðar eftir á.
                </p>
              </>
            )}
          </div>
        </section>

        <section className="card col-8">
          <div className="card-h">
            <h2>Hlið A</h2>
            <span className="lbl">Mat í mars 2027</span>
          </div>
          {stats && (
            <div className="rings">
              <Ring {...stats.gates.unique} label="Einstaklingar sem hafa mætt" />
              <Ring {...stats.gates.regulars} label="Hafa mætt 4+ sinnum" />
              <Ring {...stats.gates.average} label="Meðalmæting, síðustu 6" />
            </div>
          )}
        </section>
      </div>

      <div className="stats">
        <Stat label="Viðburðir" value={stats?.events ?? 0} />
        <Stat label="Skráðir félagar" value={stats?.registered ?? 0} />
        <Stat
          label="Meðalmæting"
          value={stats ? Math.round(stats.avgAttendance * 10) / 10 : 0}
          gold
        />
        <Stat label="Staða sjóðs" value={kr(finance?.balance ?? 0)} gold />
        <Stat label="Spilarar alls" value={stats?.members ?? 0} />
        <Stat label="Opin verkefni" value={stats?.openTodos ?? 0} />
      </div>

      <div className="g-two">
        <section className="card col-8">
          <div className="card-h">
            <h2>Síðustu viðburðir</h2>
          </div>
          {recent.length === 0 ? (
            <Empty
              title="Engir viðburðir skráðir"
              body="Byrjaðu á að skrá kvöldið sem þegar er búið — nafn, dagsetning og hverjir mættu."
            />
          ) : (
            <div className="tw">
              <table>
                <thead>
                  <tr>
                    <th>Dagsetning</th>
                    <th>Viðburður</th>
                    <th>Mæting</th>
                    <th>Sigurvegari</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((e) => (
                    <tr key={e.id}>
                      <td className="n" data-label="Dagsetning">{fmtDate(e.event_date)}</td>
                      <td data-label="Viðburður">
                        <div className="ttl2">{e.name}</div>
                        {e.format && <span className="chip">{e.format}</span>}
                      </td>
                      <td className="n" data-label="Mæting">{e.attendees.length}</td>
                      <td data-label="Sigurvegari">
                        {e.winner_member_id ? (
                          <span className="chip gold">{memberName(e.winner_member_id)}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="card col-4">
          <div className="card-h">
            <h2>Mæting</h2>
            <span className="lbl">
              {stats && stats.trend.length > 0
                ? `síðustu ${stats.trend.length}`
                : 'engir viðburðir'}
            </span>
          </div>
          {stats && stats.trend.length > 0 ? (
            <AttendanceBars data={stats.trend} target={10} />
          ) : (
            <Empty title="Engin mæting skráð" body="Súluritið birtist um leið og fyrsti viðburðurinn er skráður." />
          )}
        </section>
      </div>

      <section className="card col-8">
        <div className="card-h">
          <h2>Staða sjóðs</h2>
          <span className="lbl">{finance?.movements ?? 0} færslur</span>
        </div>
        {finance && finance.series.length > 1 ? (
          <BalanceChart series={finance.series} />
        ) : (
          <Empty
            title="Of fáar færslur fyrir graf"
            body="Skráðu styrki og kostnað undir Fjármál — ferillinn birtist þegar peningar hafa hreyfst á tveimur dögum."
          />
        )}
      </section>

      <section className="card col-4">
        <div className="card-h">
          <h2>Þín verkefni</h2>
          {overdue > 0 && <span className="lbl" style={{ color: 'var(--danger)' }}>{overdue} yfir tíma</span>}
        </div>

        {mine.length === 0 ? (
          <Empty title="Ekkert á þér" body="Engin opin verkefni skráð á þig núna." />
        ) : (
          <>
            <div className="mine-lead">
              <b>{mine.length}</b>
              <span>
                {mine.length === 1 ? 'opið verkefni' : 'opin verkefni'}
                {nextUp ? ` · næsta ${dueText(nextUp.due_date)}` : ''}
              </span>
            </div>
            <ul className="mine-list">
              {mine.slice(0, 5).map((t) => {
                const d = daysUntil(t.due_date);
                const cls = d === null ? '' : d < 0 ? ' over' : d <= 3 ? ' soon' : '';
                return (
                  <li key={t.id}>
                    <span className="mt">{t.text}</span>
                    {t.due_date && <span className={`md${cls}`}>{dueText(t.due_date)}</span>}
                  </li>
                );
              })}
            </ul>
            {mine.length > 5 && (
              <span className="lbl" style={{ marginTop: 10, display: 'block' }}>
                +{mine.length - 5} til viðbótar
              </span>
            )}
          </>
        )}
      </section>
    </div>
  );
}
